/**
 * Session context + guards (§19.1, §18). On mount we poll GET /me (which never
 * 401s — it returns { authenticated: false } when there's no session). The
 * cookie session is the security boundary on the worker; these guards are a UX
 * layer that routes by role (§18): ADMIN/MANAGER → /queue, PACKER → /pack.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useLocation } from "react-router-dom";
import type { Role } from "@cairn/shared";
import { api, type SessionUser } from "../lib/api.js";

interface SessionState {
  /** undefined while the first /me is in flight; null when unauthenticated. */
  user: SessionUser | null | undefined;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const me = await api.getMe();
      setUser(me.authenticated ? me.user : null);
    } catch {
      // /me never 401s; a thrown error means the worker is unreachable. Treat
      // as unauthenticated so the SPA shows Login rather than a blank screen.
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    await api.logout().catch(() => undefined);
    setUser(null);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<SessionState>(
    () => ({ user, loading, refresh, signOut }),
    [user, loading, refresh, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used within <SessionProvider>");
  return ctx;
}

/** Landing path for a role (§18). PACKER has only the pack station. */
export function homePathFor(role: Role): string {
  return role === "PACKER" ? "/pack" : "/queue";
}

/**
 * Gate a subtree on an authenticated session with one of `roles`. While the
 * first /me resolves we render nothing (the shell shows a thin loading bar).
 * Unauthenticated → /login. Authenticated but wrong role → that role's home.
 */
export function RequireRole({
  roles,
  children,
}: {
  roles: Role[];
  children: ReactNode;
}) {
  const { user, loading } = useSession();
  const location = useLocation();

  if (loading && user === undefined) {
    return <div className="auth-loading" aria-busy="true" />;
  }
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (!roles.includes(user.role)) {
    return <Navigate to={homePathFor(user.role)} replace />;
  }
  return <>{children}</>;
}
